<template>
    <DnInput
        :wrapper-attrs="wrapperAttr"
        :value="modelValue"
        @input="$emit('update:modelValue', $event)"
    >
        <template
            v-for="name in Object.keys($slots)"
            v-slot:[name]="scope"
        >
            <slot
                :name="name"
                v-bind="scope"
            />
        </template>
    </DnInput>
</template>

<script setup>
import DnInput from '@digitalnatives/form-input';

defineProps({
    wrapperAttr: {
        type: Object,
        default: () => ({})
    },
    modelValue: {
        type: String,
        default: ''
    }
});

defineEmits(['update:modelValue']);
</script>

<style lang="less">
.dn-input {
    .input();
}

.dn-input[data-validation='valid'] {
    .input--valid();
}

.dn-input[data-validation='invalid'] {
    .input--invalid();
}

.dn-input[data-focus='true'] {
    .input--focussed();
}

.dn-input.dn-input--rounded {
    .typography-label();
    height: 3rem;

    border-color: transparent;
    border-radius: var(--borderRadius-lg);
    box-shadow: var(--boxShadow-medium);
    line-height: 3rem;

    input[type='search']::-webkit-search-decoration,
    input[type='search']::-webkit-search-cancel-button,
    input[type='search']::-webkit-search-results-button,
    input[type='search']::-webkit-search-results-decoration {
        -webkit-appearance: none;
    }

    .dn-input__field {
        box-shadow: none;
    }

    .dn-input__field-body {
        padding: 0 1.25rem;
    }

    .dn-input__after {
        button {
            padding: 0;
            border: none;
            background: none;
            box-shadow: none;
            cursor: pointer;

            .dn-icon {
                display: block;

                &[data-icon='close'] {
                    font-size: 1.5em;
                }

                &[data-icon='search'] {
                    font-size: 1.25em;
                }
            }
        }
    }

    @media @q-md-min {
        height: 3.4375rem;
        line-height: 3.4375rem;

        /deep/ .dn-input__field-body {
            padding: 0 1.75rem;
        }

        /deep/ .dn-input__after {
            padding-right: .5rem;
        }
    }

    &:focus-within {
        border-color: var(--color-gray-300);
    }
}

.dn-input.dn-input--rounded-secondary {
    height: 2.75rem;

    font-size: var(--font-size-md);

    background-color: var(--color-secondary-light);
    border-color: transparent;
    border-radius: var(--borderRadius-lg);
    box-shadow: none;
    line-height: 2.75rem;

    .dn-input__field {
        box-shadow: none;
    }

    .dn-input__field-body {
        padding: 0 0 0 1.5rem;

        @media @q-lg {
            padding: 0 0 0 1rem;
        }
    }

    .dn-input__after {
        background-color: inherit;

        button {
            padding: 0;
            border: none;
            background: none;
            box-shadow: none;
            cursor: pointer;

            .dn-icon {
                display: block;

                path {
                    fill: none;
                    stroke: currentColor;
                }
            }

            .dn-icon[data-icon='close'] {
                font-size: 1.25rem;
            }

            .dn-icon[data-icon='search'] {
                font-size: 1.5rem;
                @media @q-lg {
                    font-size: 1.25rem;
                }
            }
        }
    }

    &:focus-within {
        @media @q-xl-min {
            border-color: var(--color-gray-300);
        }
    }
}

.input--invalid() {
    border-color: var(--color-highlight-error);
}

.input--valid() {
    border-color: var(--color-black-30);
}

.base-input.dn-input {
    .input();
}

.dn-input[data-validation='valid'],
[data-has-errors='false'][data-dirty='true'] .dn-input {
    .input--valid();
}

.dn-input[data-validation='invalid'],
[data-has-errors='true'] .dn-input {
    .input--invalid();
}

// Override weird toolkit styles
.dn-input .dn-input__after {
    background: none;
}
</style>